/* Modal global de Capacitaciones Administrativas (TD-11629).
 *
 * - Al iniciar sesión consulta /capacitacion-adm/pendientes:
 *     1) Confirmaciones: el anfitrión cargó a la persona a mano y falta que ella
 *        declare si participó o no (reemplaza la firma en la planilla).
 *     2) Evaluaciones: participaciones confirmadas con preguntas sin responder,
 *        solo de capacitaciones que ya terminaron (o con el registro cerrado).
 * - NO bloquea ninguna sección: "Más tarde" lo cierra y vuelve a aparecer en el
 *   próximo inicio mientras quede algo pendiente.
 * - También se abre al recibir el evento CAPADM_ABRIR_PENDIENTES (después de
 *   confirmar asistencia por QR, o desde "Mis capacitaciones").
 * - La corrección la hace el backend; el resultado (con las respuestas
 *   correctas) recién llega después de enviar.
 */
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRadio,
  IonRadioGroup,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import { CircularProgress, Typography } from "@material-ui/core";
import { calendarOutline, locationOutline, personOutline } from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import CapacitacionAdmAPI, {
  CAPADM_ABRIR_PENDIENTES,
  CAPADM_PENDIENTES_CAMBIARON,
  CapacitacionPublica,
  ConfirmacionPendiente,
  PreguntaEvaluacion,
  ResultadoEvaluacion,
} from "src/services/capacitacionAdm";
import { colorPuntaje, formatFechaHora, textoEvaluacionDesde } from "src/utils/capacitacionAdm";
import "src/pages/CapacitacionAdm.css";

type Vista = "cerrado" | "confirmacion" | "evaluacion" | "resultado";

interface Props {
  isAuth: boolean;
}

const avisarCambio = () => window.dispatchEvent(new Event(CAPADM_PENDIENTES_CAMBIARON));

const CapacitacionAdmPendientesModal: React.FC<Props> = ({ isAuth }) => {
  const [present] = useIonToast();
  const [presentAlert] = useIonAlert();

  const [vista, setVista] = useState<Vista>("cerrado");
  const [confirmaciones, setConfirmaciones] = useState<ConfirmacionPendiente[]>([]);
  const [evaluaciones, setEvaluaciones] = useState<CapacitacionPublica[]>([]);
  const [preguntas, setPreguntas] = useState<PreguntaEvaluacion[]>([]);
  const [errorPreguntas, setErrorPreguntas] = useState(false);
  const [respuestas, setRespuestas] = useState<Record<number, number>>({});
  const [resultado, setResultado] = useState<ResultadoEvaluacion | null>(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const yaVerificado = useRef(false);

  const actualConf = confirmaciones[0] || null;
  const actualEval = evaluaciones[0] || null;

  const cargarEvaluacion = async (cap: CapacitacionPublica) => {
    setVista("evaluacion");
    setRespuestas({});
    setPreguntas([]);
    setErrorPreguntas(false);
    setCargando(true);
    try {
      const data = await CapacitacionAdmAPI.evaluacion(cap.id);
      setPreguntas(data.preguntas || []);
    } catch {
      setErrorPreguntas(true);
    } finally {
      setCargando(false);
    }
  };

  // Decide qué mostrar con las colas actuales: primero confirmaciones, luego evaluaciones.
  const mostrarSiguiente = (conf: ConfirmacionPendiente[], evals: CapacitacionPublica[]) => {
    setConfirmaciones(conf);
    setEvaluaciones(evals);
    if (conf.length > 0) {
      setVista("confirmacion");
    } else if (evals.length > 0) {
      cargarEvaluacion(evals[0]);
    } else {
      setVista("cerrado");
    }
  };

  const consultar = async () => {
    const p = await CapacitacionAdmAPI.pendientes();
    if (!p || (p.confirmaciones.length === 0 && p.evaluaciones.length === 0)) return;
    setResultado(null);
    mostrarSiguiente(p.confirmaciones, p.evaluaciones);
  };

  useEffect(() => {
    if (!isAuth) {
      yaVerificado.current = false;
      setVista("cerrado");
      setConfirmaciones([]);
      setEvaluaciones([]);
      return;
    }
    if (yaVerificado.current) return;
    yaVerificado.current = true;
    consultar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  useEffect(() => {
    if (!isAuth) return;
    window.addEventListener(CAPADM_ABRIR_PENDIENTES, consultar);
    return () => window.removeEventListener(CAPADM_ABRIR_PENDIENTES, consultar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  // ── Confirmación ─────────────────────────────────────────────────────────
  const responder = async (participo: boolean) => {
    if (!actualConf) return;
    setGuardando(true);
    try {
      const res = await CapacitacionAdmAPI.responderPendiente(actualConf.id, participo);
      const enCurso = participo && res.tiene_evaluacion && !res.evaluacion_disponible;
      present({
        message: enCurso ? `${res.info}. ${textoEvaluacionDesde(res.fecha_fin)}` : res.info,
        duration: enCurso ? 4500 : 2500,
        color: participo ? "success" : "medium",
      });
      avisarCambio();
      // Si confirmó y la evaluación ya está habilitada, pasa al frente de la cola.
      const evals =
        participo && res.evaluacion_disponible && !evaluaciones.some((e) => e.id === actualConf.id)
          ? [actualConf, ...evaluaciones]
          : evaluaciones;
      mostrarSiguiente(confirmaciones.slice(1), evals);
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo guardar tu respuesta",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  // "No participé" no tiene vuelta atrás para el participante: se confirma.
  const confirmarRechazo = () =>
    presentAlert({
      header: "¿No participaste?",
      message: "Le avisaremos al anfitrión que no participaste de esta capacitación.",
      buttons: ["Volver", { text: "No participé", role: "destructive", handler: () => responder(false) }],
    });

  // ── Evaluación ───────────────────────────────────────────────────────────
  const respondidas = preguntas.filter((p) => respuestas[p.id] !== undefined).length;

  const enviar = async () => {
    if (!actualEval || respondidas < preguntas.length) return;
    setGuardando(true);
    try {
      const res = await CapacitacionAdmAPI.enviarEvaluacion(
        actualEval.id,
        preguntas.map((p) => ({ pregunta_id: p.id, opcion_id: respuestas[p.id] }))
      );
      setResultado(res);
      setVista("resultado");
      avisarCambio();
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo guardar la evaluación",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  const continuarTrasResultado = () => {
    setResultado(null);
    mostrarSiguiente(confirmaciones, evaluaciones.slice(1));
  };

  const saltarEvaluacion = () => mostrarSiguiente(confirmaciones, evaluaciones.slice(1));

  const totalPendientes = confirmaciones.length + evaluaciones.length;

  // ── Render ───────────────────────────────────────────────────────────────
  const renderConfirmacion = () =>
    actualConf && (
      <div style={{ padding: 16 }}>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          {actualConf.anfitrion_nombre || "El anfitrión"} te registró como participante. Confirmá si participaste.
        </Typography>
        <div className="capadm-card" style={{ marginBottom: 0 }}>
          <p className="capadm-card-titulo" style={{ fontSize: 16 }}>{actualConf.tema}</p>
          <div className="capadm-meta">
            <span>
              <IonIcon icon={calendarOutline} /> {formatFechaHora(actualConf.fecha_capacitacion)}
            </span>
            {actualConf.lugar && (
              <span>
                <IonIcon icon={locationOutline} /> {actualConf.lugar}
              </span>
            )}
            {actualConf.anfitrion_nombre && (
              <span>
                <IonIcon icon={personOutline} /> {actualConf.anfitrion_nombre}
              </span>
            )}
          </div>
          {actualConf.descripcion && (
            <p className="capadm-card-sub" style={{ margin: "10px 0 0" }}>{actualConf.descripcion}</p>
          )}
        </div>
        <div className="capadm-declaracion">“{actualConf.texto_consentimiento}”</div>
        <IonButton expand="block" onClick={() => responder(true)} disabled={guardando}>
          Sí, participé
        </IonButton>
        <IonButton expand="block" fill="clear" color="medium" onClick={confirmarRechazo} disabled={guardando}>
          No participé
        </IonButton>
      </div>
    );

  const renderEvaluacion = () => (
    <>
      {actualEval && (
        <div style={{ padding: "12px 16px 0" }}>
          <Typography variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.3 }}>
            {actualEval.tema}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {formatFechaHora(actualEval.fecha_capacitacion)}
            {actualEval.anfitrion_nombre ? ` · ${actualEval.anfitrion_nombre}` : ""}
          </Typography>
        </div>
      )}
      {cargando ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
          <CircularProgress />
        </div>
      ) : errorPreguntas || preguntas.length === 0 ? (
        <div style={{ padding: 24 }}>
          <Typography color="textSecondary">
            No pudimos cargar la evaluación. Volverá a aparecer en tu próximo inicio.
          </Typography>
          <IonButton expand="block" style={{ marginTop: 16 }} onClick={saltarEvaluacion}>
            Continuar
          </IonButton>
        </div>
      ) : (
        <div style={{ padding: 16 }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Respondé todas las preguntas. Tenés un solo intento.
          </Typography>
          {preguntas.map((p, idx) => (
            <div key={p.id} className="capadm-pregunta" style={{ background: "#fff" }}>
              <Typography variant="subtitle2" style={{ marginBottom: 4, fontWeight: 600 }}>
                {idx + 1}. {p.texto}
              </Typography>
              <IonList lines="none" style={{ margin: "0 -12px" }}>
                <IonRadioGroup
                  value={respuestas[p.id] ?? null}
                  onIonChange={(e) => setRespuestas((prev) => ({ ...prev, [p.id]: Number(e.detail.value) }))}
                >
                  {p.opciones.map((o) => (
                    <IonItem key={o.id}>
                      <IonLabel style={{ whiteSpace: "normal" }}>{o.texto}</IonLabel>
                      <IonRadio slot="start" value={o.id} />
                    </IonItem>
                  ))}
                </IonRadioGroup>
              </IonList>
            </div>
          ))}
          <div className="capadm-progreso">
            {respondidas} de {preguntas.length} respondidas
          </div>
          <IonButton expand="block" onClick={enviar} disabled={guardando || respondidas < preguntas.length}>
            {guardando ? "Enviando..." : "Enviar evaluación"}
          </IonButton>
        </div>
      )}
    </>
  );

  const renderResultado = () =>
    resultado && (
      <div style={{ padding: 16 }}>
        <div
          style={{
            textAlign: "center",
            background: colorPuntaje(resultado.puntaje),
            color: "#fff",
            borderRadius: 12,
            padding: "16px",
            marginBottom: 20,
          }}
        >
          <Typography variant="h4" style={{ fontWeight: 700 }}>
            {Math.round(resultado.puntaje)}%
          </Typography>
          <Typography variant="body2">
            {resultado.correctas} de {resultado.total} correctas
          </Typography>
          <Typography variant="caption" style={{ opacity: 0.9 }}>
            {resultado.tema}
          </Typography>
        </div>
        {resultado.detalle.map((d, i) => (
          <div
            key={i}
            style={{
              background: d.es_correcta ? "#e8f5e9" : "#ffebee",
              borderLeft: `4px solid ${colorPuntaje(d.es_correcta ? 100 : 0)}`,
              borderRadius: 6,
              padding: "8px 12px",
              marginBottom: 8,
            }}
          >
            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
              {i + 1}. {d.pregunta}
            </Typography>
            <Typography variant="body2" style={{ color: colorPuntaje(d.es_correcta ? 100 : 0) }}>
              Tu respuesta: {d.respuesta}
            </Typography>
            {!d.es_correcta && d.correcta && (
              <Typography variant="body2" style={{ color: "#2e7d32" }}>
                Correcta: {d.correcta}
              </Typography>
            )}
          </div>
        ))}
        <IonButton expand="block" style={{ marginTop: 8 }} onClick={continuarTrasResultado}>
          {totalPendientes > 1 ? "Continuar" : "Cerrar"}
        </IonButton>
      </div>
    );

  const titulo =
    vista === "confirmacion" ? "Confirmá tu participación" : vista === "resultado" ? "Tu resultado" : "Evaluación";

  return (
    <IonModal isOpen={vista !== "cerrado"} backdropDismiss={false}>
      <IonHeader>
        <IonToolbar>
          <IonTitle style={{ fontSize: 16 }}>
            {titulo}
            {vista !== "resultado" && totalPendientes > 1 ? ` (1 de ${totalPendientes})` : ""}
          </IonTitle>
          {vista !== "resultado" && (
            <IonButtons slot="end">
              <IonButton onClick={() => setVista("cerrado")} disabled={guardando}>
                Más tarde
              </IonButton>
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {vista === "confirmacion" && renderConfirmacion()}
        {vista === "evaluacion" && renderEvaluacion()}
        {vista === "resultado" && renderResultado()}
      </IonContent>
    </IonModal>
  );
};

export default CapacitacionAdmPendientesModal;
